import {
  BenefitFormData,
  CircuitDayFormData,
} from '@/types/circuits/CircuitDaysFormData';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { RadioGroup } from '@/components/ui/radio-group';
import { OptionalBenefitComputationFn } from '../EstimateDetailsAndOptions';
import { SelectedEstimateOption } from '../../../types/estimates/SelectedEstimateOptionType';
import { useEstimatePrices } from '@/hooks/useEstimatePrices';
import { useGetFileUrl } from '@/hooks/useFile';
import EstimateDayStepCardTemplate from './EstimateDayStepCardTemplate';
import EstimateOptionalBenefitCell from './EstimateOptionalBenefitCell';
import { SupportedLocale } from '@/types/supported-locale.type';
import { sanitizeDeltaPrice } from '@/utils/circuits/prices/formatPrice';

export interface EstimateDayStepCardProps {
  step: CircuitDayFormData['steps'][number]['transport'];
  date: Date;
  dayIndex: number;
  computeOptionalBenefitPrice: OptionalBenefitComputationFn;
  selectedOptions: Record<string, SelectedEstimateOption>;
  setSelectedOptions: Dispatch<
    SetStateAction<Record<string, SelectedEstimateOption>>
  >;
  currency?: string;
  creationDate?: Date;
  nbParticipants?: number;
  accessToken?: string;
  isViewMode?: boolean;
  language?: SupportedLocale;
}

const EstimateDayStepCard = ({
  step,
  date,
  dayIndex,
  computeOptionalBenefitPrice,
  selectedOptions,
  setSelectedOptions,
  nbParticipants,
  currency = 'EUR',
  creationDate,
  accessToken,
  isViewMode = false,
  language,
}: EstimateDayStepCardProps) => {
  const { type, computeBenefitPriceDelta, stepDates } = useEstimatePrices({
    step,
    computeOptionalBenefitPrice,
    date,
    dayIndex,
  });

  const defaultSelectedOption = useMemo(
    () =>
      Object.hasOwn(selectedOptions, step.id) ? selectedOptions[step.id] : null,
    [selectedOptions, step.id],
  );

  const optionalBenefitsWithDeltaPrice = useMemo(
    () =>
      step.optionalBenefits
        .map((benefit) => {
          const deltaPrice = sanitizeDeltaPrice(
            computeBenefitPriceDelta(benefit),
          );
          return { benefit, deltaPrice };
        })
        .toSorted((a, b) => a.deltaPrice - b.deltaPrice),
    [computeBenefitPriceDelta, step.optionalBenefits],
  );

  const images = useMemo(() => {
    const standard = step.standardBenefit;
    return (
      standard?.activity?.images ?? standard?.VehicleWithChauffeur?.images ?? []
    );
  }, [step.standardBenefit]);

  const mainImageKey = useMemo(() => {
    return images.length > 0 ? images[0] : null;
  }, [images]);

  const getImagesFromOptionalBenefit = useCallback(
    (benefit: BenefitFormData) => {
      return benefit?.activity?.images ?? benefit?.VehicleWithChauffeur?.images;
    },
    [],
  );

  const getMainImageKeyFromOptionalBenefit = useCallback(
    (benefit: BenefitFormData) => {
      const benefitImages = getImagesFromOptionalBenefit(benefit);
      return benefitImages && benefitImages.length > 0
        ? benefitImages[0]
        : null;
    },
    [getImagesFromOptionalBenefit],
  );

  const { data: imageUrl } = useGetFileUrl(mainImageKey, false, accessToken);

  const [selectedOptionalBenefitId, setSelectedOptionalBenefitId] = useState<
    string | null
  >(defaultSelectedOption?.benefitId ?? null);

  const [displayOptionalBenefits, setDisplayOptionalBenefits] = useState(
    !!selectedOptionalBenefitId,
  );

  const selectOptionalBenefitGenerator = useCallback(
    (benefit: BenefitFormData, deltaPrice: number) => () => {
      setSelectedOptions((prev) => {
        if (prev[step.id] && prev[step.id].benefitId === benefit.id) {
          setSelectedOptionalBenefitId(null);
          // oxlint-disable-next-line @typescript-eslint/no-unused-vars
          const { [step.id]: _, ...rest } = prev;
          return rest;
        }
        setSelectedOptionalBenefitId(benefit.id);
        return {
          ...prev,
          [step.id]: {
            benefitId: benefit.id,
            type,
            deltaPrice: sanitizeDeltaPrice(deltaPrice),
          } as SelectedEstimateOption,
        };
      });
    },
    [setSelectedOptions, step.id, type],
  );

  const standardName = useMemo(() => {
    return language
      ? (step.standardBenefit?.translations?.[language]?.name ??
          step.standardBenefit?.name ??
          '')
      : (step.standardBenefit?.name ?? '');
  }, [
    language,
    step.standardBenefit?.name,
    step.standardBenefit?.translations,
  ]);

  return (
    <EstimateDayStepCardTemplate
      step={step}
      type={type}
      stepDates={stepDates}
      standardName={standardName}
      displayOptionalBenefits={displayOptionalBenefits}
      setDisplayOptionalBenefits={setDisplayOptionalBenefits}
      imageUrl={imageUrl?.url}
      imagesKeys={images}
      nbParticipants={nbParticipants}
      currency={currency}
      accessToken={accessToken}
      language={language}
    >
      <RadioGroup
        disabled={isViewMode}
        defaultValue={defaultSelectedOption?.benefitId}
      >
        {optionalBenefitsWithDeltaPrice.map(
          ({ benefit, deltaPrice }, indexBenefit) => (
            <EstimateOptionalBenefitCell
              nbParticipants={nbParticipants}
              optionalDisplays={step.optionalDisplays}
              benefit={benefit}
              deltaPrice={deltaPrice}
              isChecked={selectedOptionalBenefitId === benefit.id}
              selectOptionalBenefit={selectOptionalBenefitGenerator(
                benefit,
                deltaPrice,
              )}
              imagesKeys={getImagesFromOptionalBenefit(benefit) ?? undefined}
              mainImageKey={
                getMainImageKeyFromOptionalBenefit(benefit) ?? undefined
              }
              currency={currency}
              creationDate={creationDate}
              accessToken={accessToken}
              language={language}
              key={`day-${dayIndex}-optionalBenefit-${indexBenefit}-${benefit.id}`}
            />
          ),
        )}
      </RadioGroup>
    </EstimateDayStepCardTemplate>
  );
};

export default EstimateDayStepCard;
